import type { FamilyDto } from './types'

/**
 * One bucket of catalog families that share the same `familyGroup` key.
 * The group's display label is the BE-translated `familyGroupLabel` carried by every
 * variant in the group; the group's icon is the icon of the first (lowest-DisplayOrder)
 * variant — for mono-product groups they are identical anyway.
 */
export interface FamilyGroup {
  groupKey: string
  groupLabel: string
  icon: string
  groupOrder: number
  variants: FamilyDto[]
}

/**
 * Bucket FamilyDtos by `familyGroup`, then sort groups by the BE-provided
 * `familyGroupOrder` (= `Min(DisplayOrder)` across the group's variants). Sorting
 * by an explicit field avoids the previous insertion-order behaviour, where group
 * order depended on the alphabetical Code tie-break of the lowest-DisplayOrder
 * variant in the BE response (which surfaced "Électro/Sanitaire" before
 * "Plan de travail"). For mono-product groups (variants.length === 1) the group is
 * effectively just the single product — the picker UI treats those as one-click shortcuts.
 */
export function groupFamilies(families: ReadonlyArray<FamilyDto>): FamilyGroup[] {
  const byKey = new Map<string, FamilyGroup>()

  for (const f of families) {
    const key = f.familyGroup || f.familyKey
    const existing = byKey.get(key)
    if (existing) {
      existing.variants.push(f)
    } else {
      byKey.set(key, {
        groupKey: key,
        groupLabel: f.familyGroupLabel || f.familyLabel,
        icon: f.icon,
        // `?? 0` defends against a stale BE response (e.g. FE deployed before BE) where
        // the field is missing — without it, `undefined - undefined` produces NaN and the
        // sort silently no-ops, regressing to the bug this field was added to fix.
        groupOrder: f.familyGroupOrder ?? 0,
        variants: [f],
      })
    }
  }

  return Array.from(byKey.values()).sort((a, b) => a.groupOrder - b.groupOrder)
}

/**
 * Filter applied at both levels of the picker. Matches the query against the group label
 * AND every variant label, so a search like "lave" finds both the `appliance_dishwasher`
 * variant and the `base_unit_dishwasher` variant without the user having to know which
 * group they belong to.
 *
 * Returns groups where either the group label matches OR at least one variant matches.
 * Variant filtering happens inside each group when the user is viewing that group's
 * variants — see `filterVariants`.
 */
export function filterGroups(groups: FamilyGroup[], query: string): FamilyGroup[] {
  const q = query.trim().toLowerCase()
  if (!q) return groups

  return groups.filter(
    (g) =>
      g.groupLabel.toLowerCase().includes(q) ||
      g.variants.some((v) => v.familyLabel.toLowerCase().includes(q))
  )
}

export function filterVariants(variants: FamilyDto[], query: string): FamilyDto[] {
  const q = query.trim().toLowerCase()
  if (!q) return variants

  return variants.filter((v) => v.familyLabel.toLowerCase().includes(q))
}
